import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'

type Feature = {
  name: string
  format: string
  description: string
}

interface FeatureDescriptionImportModalProps {
  isOpen: boolean
  onClose: () => void
  onImport: (features: Feature[]) => void
}

export const FeatureDescriptionImportModal: React.FC<FeatureDescriptionImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
}) => {
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setText('')
      setError(null)
    }
  }, [isOpen])

  const parsedLines = useMemo(() => {
    return text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
  }, [text])

  const handleImport = () => {
    if (parsedLines.length === 0) {
      setError('Please provide at least one line with "feature, description".')
      return
    }

    try {
      const features = parsedLines.map((line, idx) => {
        const [rawName, ...descParts] = line.split(',')
        const name = rawName?.trim() || ''
        const description = descParts.join(',').trim()

        if (!name) {
          throw new Error(`Line ${idx + 1}: feature name is required.`)
        }

        return {
          name,
          description,
          format: 'double', // default format
        }
      })

      onImport(features)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid input format.')
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Batch Import Features with Description</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <FormControl>
              <FormLabel>Feature, Description</FormLabel>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={'price, Closing price\nvolume, 24h volume'}
                rows={10}
              />
              <Text mt={2} color="gray.600" fontSize="sm">
                Each line: feature_name, description. Format defaults to double.
              </Text>
            </FormControl>

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleImport}>
            Import
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

